import { useDispatch, useSelector } from 'react-redux';
import {
  getIsInComparison,
  getOperationProductsCompareListsIsLoading,
  getProductsCompareLists,
  getProductsCompareListsState,
} from 'features/products-compare/ducks/selectors';
import { useComparisonListStatus } from './use-comparison-list-status';
import {
  MANAGE_PRODUCT_IN_COMPARE_LIST,
  RESET_VALIDATION,
  SEND_PRODUCTS_COMPARE_ACTION,
  SET_VALIDATION_ERROR,
} from 'features/products-compare/ducks/actions';
import { CompareActions } from 'features/products-compare/ducks/types';
import { getProductsCompareListLimit, getProductsCompareListsLimit } from 'common/selectors';
import { Product } from 'common/components/product-card/types';
import { sendGAComparisonEvent } from '../utils';
import { useTracking } from 'features/tracking/use-tracking';
import { SponsoredProductType } from 'features/products/types';

export const useCompareClick = (product: Product, productPosition: number, elementBlock: string) => {
  const dispatch = useDispatch();
  const listsState = useSelector(getProductsCompareListsState);
  const isLoadingOperation = useSelector(getOperationProductsCompareListsIsLoading);
  const isError = !isLoadingOperation && listsState === 'error';

  const compareLists = useSelector(getProductsCompareLists);
  const compareListLimit = useSelector(getProductsCompareListLimit);
  const compareListsLimit = useSelector(getProductsCompareListsLimit);

  const { productId, compareCategory } = product;
  const groupName = compareCategory?.name ?? '';
  const familyId = compareCategory?.familyId ?? '';
  const isInComparison = useSelector(getIsInComparison(familyId ?? '', productId));

  const { showStatus, hideStatus, setClickHandler } = useComparisonListStatus();
  const contentId = (product as SponsoredProductType)?.contentId;
  const { sendClickEvent } = useTracking(contentId);

  const handleRemoveCompareList = () => {
    const currentGroup = compareLists?.find((list) => list.groupId === familyId);
    const isLastProduct = currentGroup?.products?.length === 1;

    if (isLastProduct) {
      dispatch(
        MANAGE_PRODUCT_IN_COMPARE_LIST({
          action: CompareActions.DELETE_GROUP,
          productId,
          familyId,
          groupName,
          withData: false,
        }),
      );

      dispatch(
        SEND_PRODUCTS_COMPARE_ACTION({
          action: CompareActions.DELETE,
          productId,
          familyId,
          groupId: familyId,
          groupName,
        }),
      );
    } else {
      dispatch(
        SEND_PRODUCTS_COMPARE_ACTION({
          action: CompareActions.DELETE,
          productId,
          familyId,
          groupId: familyId,
          groupName,
        }),
      );

      dispatch(
        MANAGE_PRODUCT_IN_COMPARE_LIST({
          action: CompareActions.DELETE,
          productId,
          familyId,
          groupName,
          withData: false,
        }),
      );
    }
  };
  const handleAddCompareList = () => {
    dispatch(
      SEND_PRODUCTS_COMPARE_ACTION({
        action: CompareActions.ADD,
        productId,
        familyId,
        groupId: familyId,
        groupName,
        withData: false,
      }),
    );

    dispatch(
      MANAGE_PRODUCT_IN_COMPARE_LIST({
        action: CompareActions.ADD,
        productId,
        familyId,
        groupName,
        withData: false,
      }),
    );
  };
  const handleCompareList = () => {
    sendGAComparisonEvent({
      product,
      productPosition,
      elementBlock,
      isInComparison,
    });
    sendClickEvent();

    if (isInComparison) {
      handleRemoveCompareList();
      dispatch(RESET_VALIDATION());
      showStatus(isError ? 'errorRemove' : 'removed');
    } else {
      const currentGroup = compareLists?.find((list) => list.groupId === familyId);
      const isProductListCountInvalid = currentGroup && currentGroup.products.length >= compareListLimit;
      const isListsCountInvalid = !currentGroup && compareLists.length >= compareListsLimit;

      if (isListsCountInvalid) {
        dispatch(SET_VALIDATION_ERROR('listsCount'));
        showStatus('listCountAlert');
      }
      if (isProductListCountInvalid) {
        dispatch(SET_VALIDATION_ERROR('productListCount'));
        showStatus('productListCountAlert');
      }
      if (!compareLists.length || !(isProductListCountInvalid || isListsCountInvalid)) {
        handleAddCompareList();
        showStatus(isError ? 'errorAdd' : 'added');
      }
    }
  };

  setClickHandler((statusType, statusId) => {
    switch (statusType) {
      case 'added':
      case 'productListCountAlert':
      case 'listCountAlert':
        hideStatus(statusId);
        break;
      case 'removed':
      case 'errorAdd':
      case 'errorRemove':
        handleCompareList();
        break;
    }
  });

  return {
    clickHandler: handleCompareList,
  };
};
